import { useState, useEffect, useCallback } from 'react';

/**
 * Хук для работы с localStorage
 * @param {string} key - Ключ для сохранения в localStorage
 * @param {any} initialValue - Начальное значение
 * @returns {[any, function]} - Массив с текущим значением и функцией для его обновления
 */
export const useLocalStorage = (key, initialValue) => {
    // Получаем значение из localStorage или используем начальное значение
    const [storedValue, setStoredValue] = useState(() => {
        try {
            const item = window.localStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        } catch (error) {
            console.error(`Error reading localStorage key "${key}":`, error);
            return initialValue;
        }
    });

    // Функция для обновления значения в localStorage
    const setValue = useCallback((value) => {
        try {
            // Позволяем value быть функцией, чтобы у нас была та же API, что и у useState
            const valueToStore = value instanceof Function ? value(storedValue) : value;
            setStoredValue(valueToStore);
            window.localStorage.setItem(key, JSON.stringify(valueToStore));
        } catch (error) {
            console.error(`Error setting localStorage key "${key}":`, error);
        }
    }, [key, storedValue]);

    // Слушаем изменения в localStorage от других вкладок
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === key && e.newValue !== null) {
                try {
                    setStoredValue(JSON.parse(e.newValue));
                } catch (error) {
                    console.error(`Error parsing localStorage value for key "${key}":`, error);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [key]);

    return [storedValue, setValue];
};

/**
 * Хук для работы с Easy Mode прогрессом
 * @returns {object} - Объект с методами для работы с прогрессом
 */
export const useEasyModeProgress = () => {
    const [progress, setProgress] = useLocalStorage('easyModeProgress', {
        currentStep: 1,
        completedSteps: [],
        animation: true
    });

    const completeStep = useCallback((stepNumber) => {
        setProgress(prev => ({
            ...prev,
            completedSteps: prev.completedSteps.includes(stepNumber) 
                ? prev.completedSteps 
                : [...prev.completedSteps, stepNumber]
        }));
    }, [setProgress]);

    const setCurrentStep = useCallback((step) => {
        setProgress(prev => ({
            ...prev,
            currentStep: step
        }));
    }, [setProgress]);

    const setAnimation = useCallback((state) => {
        setProgress(prev => ({
            ...prev,
            animation: state
        }));
    }, [setProgress]);

    const reset = useCallback(() => {
        setProgress({
            currentStep: 1,
            completedSteps: [],
            animation: true
        });
    }, [setProgress]);

    const clearProgress = useCallback(() => {
        window.localStorage.removeItem('easyModeProgress');
        reset();
    }, [reset]);

    const getProgressPercentage = useCallback(() => {
        const totalSteps = 3;
        return Math.round((progress.completedSteps.length / totalSteps) * 100);
    }, [progress.completedSteps.length]);

    const isAllStepsCompleted = useCallback(() => {
        return progress.completedSteps.length >= 3;
    }, [progress.completedSteps.length]);

    return {
        progress,
        completeStep,
        setCurrentStep,
        setAnimation,
        reset,
        clearProgress,
        getProgressPercentage,
        isAllStepsCompleted
    };
}; 